
import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";
import NotificationAlert from "react-notification-alert";

// core components
import AdminNavbarTheme1 from "components/Navbars/AdminNavbarTheme1.js";
import AdminNavbarTheme2 from "components/Navbars/AdminNavbarTheme2.js";
import Footer from "components/Footer/Footer.js";
import SidebarTheme1 from "components/Sidebar/SidebarTheme1.js";
import SidebarTheme2 from "components/Sidebar/SidebarTheme2.js";

import buildRoutes from "routes.js";
import { useTranslation } from 'react-i18next';

import { useGetAccount } from "api/account/service";
import { useQueryClient } from "@tanstack/react-query";
import { clearAuthSession } from "utils/authSession";
import { NotificationProvider } from "../../contexts/NotificationContext";
import { getActiveColorStyle } from "../../utils/colorUtils";
import {
  getShellThemeFromStorage,
  SHELL_THEME_CHANGED_EVENT,
  applyTheme2SidebarNavTypographyToDocument,
  THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT,
  THEME2_SIDEBAR_BG_CHANGED_EVENT,
  applyTheme2SidebarBackgroundToDocument,
} from "../../utils/sidebarShellTheme";
import {
  applyReactTableThemeToDocument,
  REACT_TABLE_THEME_CHANGED_EVENT,
} from "../../utils/reactTableTheme";
import ChannelsEmbedded from "../../views/settings/Channels/ChannelsEmbedded";
import { useDeployVersionPing } from "hooks/useDeployVersionPing";
import { filterSuperAdminWebMakerSidebarViews } from "views/web-maker/utils/webMakerNavigation";
import { isWebMakerRouteBlocked } from "utils/permissionUtils";


var ps;

const Admin = (props) => {
  useDeployVersionPing({ intervalMs: 600000 });

  const getCustomStateColor = (hexColor) => {
    if (!hexColor || !/^#?[0-9A-Fa-f]{6}$/.test(hexColor)) {
      return "#0092e0";
    }
    const normalized = hexColor.startsWith("#") ? hexColor : `#${hexColor}`;
    const red = parseInt(normalized.slice(1, 3), 16);
    const green = parseInt(normalized.slice(3, 5), 16);
    const blue = parseInt(normalized.slice(5, 7), 16);
    const lighter = (channel) => Math.min(255, Math.floor(channel + (255 - channel) * 0.22));
    return `#${lighter(red).toString(16).padStart(2, "0")}${lighter(green).toString(16).padStart(2, "0")}${lighter(blue).toString(16).padStart(2, "0")}`.toUpperCase();
  };

  const [shellThemeVariant, setShellThemeVariant] = React.useState(() =>
    getShellThemeFromStorage()
  );

  const [activeColor, setActiveColor] = React.useState(() => {
    let savedColor = localStorage.getItem("sidebarColor");
    if (!savedColor) {
      savedColor = "tertiary";
      localStorage.setItem("sidebarColor", savedColor);
    }
    return savedColor;
  });

  const resolvedActiveColor = shellThemeVariant === "2" ? "custom" : activeColor;
  const activeColorStyle = useMemo(() => getActiveColorStyle(resolvedActiveColor), [resolvedActiveColor]);

  // Tema renklerini CSS değişkenlerine eşleyen fonksiyon
  const updateCSSVariables = useMemo(() => (color) => {
    if (color === "custom") {
      const customColor = localStorage.getItem("sidebarCustomColor") || "#0092e0";
      const customStateColor = getCustomStateColor(customColor);
      document.documentElement.style.setProperty("--theme-custom", customColor);
      document.documentElement.style.setProperty("--theme-custom-states", customStateColor);
      document.documentElement.style.setProperty("--info", customColor);
      document.documentElement.style.setProperty("--info-states", customStateColor);
      return;
    }

    const colorMap = {
      primary: {
        info: "var(--primary)",
        infoStates: "var(--primary-states)"
      },
      secondary: {
        info: "var(--secondary)",
        infoStates: "var(--secondary-states)"
      },
      tertiary: {
        info: "var(--tertiary)",
        infoStates: "var(--tertiary-states)"
      },
      quaternary: {
        info: "var(--quaternary)",
        infoStates: "var(--quaternary-states)"
      },
      quinary: {
        info: "var(--quinary)",
        infoStates: "var(--quinary-states)"
      },
      fifth: {
        info: "var(--fifth)",
        infoStates: "var(--fifth-states)"
      },
      sixth: {
        info: "var(--sixth)",
        infoStates: "var(--sixth-states)"
      },
      seventh: {
        info: "var(--seventh)",
        infoStates: "var(--seventh-states)"
      },
      eighth: {
        info: "var(--eighth)",
        infoStates: "var(--eighth-states)"
      },
      ninth: {
        info: "var(--ninth)",
        infoStates: "var(--ninth-states)"
      }
    };

    const selectedColor = colorMap[color] || colorMap.secondary;

    // CSS değişkenleri güncellendi
    document.documentElement.style.setProperty('--info', selectedColor.info);
    document.documentElement.style.setProperty('--info-states', selectedColor.infoStates);
  }, []);
  const [sidebarMini, setSidebarMini] = React.useState(() => {
    const saved = localStorage.getItem("sidebarMini");
    return saved ? JSON.parse(saved) : true;
  });
  /** After width transition (~350ms); delays mini icon/text layout so icons don't jump during sidebar shrink. */
  const [sidebarMiniLayoutReady, setSidebarMiniLayoutReady] = React.useState(true);
  const sidebarCollapseLayoutTimerRef = React.useRef(null);
  const [opacity, setOpacity] = React.useState(0);
  const [sidebarOpened, setSidebarOpened] = React.useState(false);
  const mainPanelRef = React.useRef(null);
  const notificationAlertRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(true);
  const { i18n } = useTranslation();
  const routes = React.useMemo(() => buildRoutes(), [i18n.language]);
  const token = localStorage.getItem("token");
  const queryClient = useQueryClient();
  const { data: account, isFetched: accountFetched, isError, error } = useGetAccount();
  const [isLightTheme, setIsLightTheme] = useState(false);

  const sidebarRoutes = React.useMemo(() => {
    if (!account) return routes;
    if (account.is_super_admin) {
      return routes
        .filter(
          (route) =>
            route.path === "/tenant" ||
            route.path === "/logs" ||
            route.state === "webMakerCollapse" ||
            route.permission === "web_maker" ||
            route.permission === "super_admin_tenant_read" ||
            route.path?.startsWith("/super-admin/")
        )
        .map((route) => {
          if (route.state !== "webMakerCollapse") return route;
          return {
            ...route,
            // Preserve the previous superadmin exclusion of Inventory Website,
            // but expose Web Maker pages through the Website navbar collapse.
            views: filterSuperAdminWebMakerSidebarViews(route.views),
          };
        });
    }
    // Normal kullanıcılar super admin rotalarını görmez;
    // web_enabled false ise Website (web_maker) menüsü de gizlenir
    return routes.filter(
      (route) =>
        route.permission !== "super_admin_tenant_read" &&
        !route.path?.startsWith("/super-admin/") &&
        route.path !== "/tenant" &&
        !isWebMakerRouteBlocked(account, route)
    );
  }, [routes, account]);

  // Move showNavbarButton above useEffect hooks
  const showNavbarButton = () => {
    if (
      document.documentElement.scrollTop > 50 ||
      document.scrollingElement.scrollTop > 50 ||
      (mainPanelRef.current && mainPanelRef.current.scrollTop > 50)
    ) {
      setOpacity(1);
    } else if (
      document.documentElement.scrollTop <= 50 ||
      document.scrollingElement.scrollTop <= 50 ||
      (mainPanelRef.current && mainPanelRef.current.scrollTop <= 50)
    ) {
      setOpacity(0);
    }
  };


  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Initialize theme based on preference or default
    let savedDarkMode = localStorage.getItem("darkMode");

    if (savedDarkMode === null) {
      savedDarkMode = "false";
      localStorage.setItem("darkMode", "false");
    }

    const isDark = JSON.parse(savedDarkMode);
    if (isDark) {
      document.body.classList.remove("white-content");
    } else {
      document.body.classList.add("white-content");
    }

    return () => observer.disconnect();
  }, []);
  React.useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return undefined;
    }
    if (!accountFetched) {
      return undefined;
    }
    const status = error?.response?.status;
    const authFailed =
      account?.session_expired === true ||
      isError ||
      !account ||
      status === 401 ||
      status === 403;
    if (authFailed) {
      clearAuthSession(queryClient);
      window.location.replace("/login");
      return undefined;
    }
    if (account?.jwt) {
      localStorage.setItem("token", account.jwt);
    }
    setIsLoading(false);
    return undefined;
  }, [token, account, accountFetched, isError, error, queryClient]);

  React.useEffect(() => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      navigate("/login", { replace: true });
    }
  }, [location, account, navigate]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainPanelRef.current) {
      mainPanelRef.current.scrollTop = 0;
    }
  }, [location]);

  React.useEffect(() => {
    const syncShellTheme = () => {
      setShellThemeVariant(getShellThemeFromStorage());
    };
    window.addEventListener(SHELL_THEME_CHANGED_EVENT, syncShellTheme);
    window.addEventListener("storage", syncShellTheme);
    return () => {
      window.removeEventListener(SHELL_THEME_CHANGED_EVENT, syncShellTheme);
      window.removeEventListener("storage", syncShellTheme);
    };
  }, []);

  React.useEffect(() => {
    applyTheme2SidebarNavTypographyToDocument();
    const syncTypo = () => applyTheme2SidebarNavTypographyToDocument();
    window.addEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncTypo);
    window.addEventListener("storage", syncTypo);
    return () => {
      window.removeEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncTypo);
      window.removeEventListener("storage", syncTypo);
    };
  }, []);

  React.useEffect(() => {
    applyTheme2SidebarBackgroundToDocument();
    const syncSidebarBg = () => applyTheme2SidebarBackgroundToDocument();
    window.addEventListener(SHELL_THEME_CHANGED_EVENT, syncSidebarBg);
    window.addEventListener(THEME2_SIDEBAR_BG_CHANGED_EVENT, syncSidebarBg);
    window.addEventListener("storage", syncSidebarBg);
    return () => {
      window.removeEventListener(SHELL_THEME_CHANGED_EVENT, syncSidebarBg);
      window.removeEventListener(THEME2_SIDEBAR_BG_CHANGED_EVENT, syncSidebarBg);
      window.removeEventListener("storage", syncSidebarBg);
    };
  }, []);

  React.useEffect(() => {
    applyReactTableThemeToDocument();
    const syncTableTheme = () => applyReactTableThemeToDocument();
    window.addEventListener(REACT_TABLE_THEME_CHANGED_EVENT, syncTableTheme);
    window.addEventListener("storage", syncTableTheme);
    return () => {
      window.removeEventListener(REACT_TABLE_THEME_CHANGED_EVENT, syncTableTheme);
      window.removeEventListener("storage", syncTableTheme);
    };
  }, []);

  React.useEffect(() => {
    const syncOnBodyClass = () => {
      applyReactTableThemeToDocument();
      applyTheme2SidebarBackgroundToDocument();
    };
    const observer = new MutationObserver(syncOnBodyClass);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (shellThemeVariant === "2") {
      updateCSSVariables("custom");
    } else {
      updateCSSVariables(activeColor);
    }
  }, [shellThemeVariant, activeColor, updateCSSVariables]);

  React.useEffect(() => {
    // Loading tamamlanana kadar bekle
    if (isLoading || !account) {
      return;
    }

    let innerMainPanelRef = mainPanelRef;
    if (navigator.platform.indexOf("Win") > -1) {
      document.documentElement.classList.add("perfect-scrollbar-on");
      document.documentElement.classList.remove("perfect-scrollbar-off");

      // Güvenli kontrol: mainPanelRef.current var mı kontrol et
      if (mainPanelRef.current) {
        ps = new PerfectScrollbar(mainPanelRef.current);
        mainPanelRef.current.addEventListener("ps-scroll-y", showNavbarButton);
      }

      let tables = document.querySelectorAll(".table-responsive");
      for (let i = 0; i < tables.length; i++) {
        if (tables[i]) {
          try {
            ps = new PerfectScrollbar(tables[i]);
          } catch (error) {
            console.warn("PerfectScrollbar initialization failed for table:", error);
          }
        }
      }
    }
    window.addEventListener("scroll", showNavbarButton);
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1 && ps) {
        try {
          ps.destroy();
        } catch (error) {
          console.warn("PerfectScrollbar destroy failed:", error);
        }
        document.documentElement.classList.add("perfect-scrollbar-off");
        document.documentElement.classList.remove("perfect-scrollbar-on");
        innerMainPanelRef.current &&
          innerMainPanelRef.current.removeEventListener(
            "ps-scroll-y",
            showNavbarButton
          );
      }
      window.removeEventListener("scroll", showNavbarButton);
    };
  }, []);

  // Sidebar rengini localStorage'dan oku
  useEffect(() => {
    const savedColor = localStorage.getItem("sidebarColor");
    if (savedColor) {
      setActiveColor(savedColor);
    }
  }, []);

  useEffect(() => {
    // Component mount olduğunda body class'ı sidebarMini'ye göre ayarlanıyor
    if (sidebarMini) {
      document.body.classList.add("sidebar-mini");
    } else {
      document.body.classList.remove("sidebar-mini");
    }
  }, [sidebarMini]);

  useEffect(() => {
    if (sidebarMini && sidebarMiniLayoutReady) {
      document.body.classList.add("sidebar-mini-layout-done");
    } else {
      document.body.classList.remove("sidebar-mini-layout-done");
    }
  }, [sidebarMini, sidebarMiniLayoutReady]);

  useEffect(
    () => () => {
      if (sidebarCollapseLayoutTimerRef.current) {
        clearTimeout(sidebarCollapseLayoutTimerRef.current);
      }
    },
    []
  );

  const normalizedPath = location.pathname.replace(/\/$/, "");
  const isTheme2InventoryDetail = /^\/inventory\/[^/]+$/.test(normalizedPath);
  const isTheme2DealDetail = /^\/deals\/[^/]+$/.test(normalizedPath);
  const isTheme2CustomerDetail =
    /^\/customers\/(?!add$)[^/]+$/.test(normalizedPath);
  const isTheme2RolesEdit = /^\/roles\/edit\/[^/]+$/.test(normalizedPath);
  const isTheme2ReportDetail = /^\/reports\/[^/]+$/.test(normalizedPath);
  const theme2HideMainShellScrollbars =
    shellThemeVariant === "2" &&
    (normalizedPath === "/dashboard" ||
      normalizedPath === "/search-inventory" ||
      normalizedPath === "/deals" ||
      normalizedPath === "/accounts" ||
      normalizedPath === "/customers" ||
      normalizedPath === "/reports" ||
      normalizedPath === "/daily-recap" ||
      normalizedPath === "/standard-tax-reports" ||
      normalizedPath === "/logs" ||
      normalizedPath === "/institution" ||
      normalizedPath === "/roles" ||
      normalizedPath === "/portfolios" ||
      normalizedPath === "/channels" ||
      normalizedPath === "/api-permissions" ||
      normalizedPath === "/flags" ||
      normalizedPath === "/inventory-parameters" ||
      normalizedPath === "/website-performance" ||
      normalizedPath === "/email-settings" ||
      normalizedPath === "/payment-settings" ||
      normalizedPath === "/communication-templates" ||
      normalizedPath === "/users" ||
      normalizedPath === "/sidebar-appearance" ||
      normalizedPath === "/change-password" ||
      isTheme2InventoryDetail ||
      isTheme2DealDetail ||
      isTheme2CustomerDetail ||
      isTheme2RolesEdit ||
      isTheme2ReportDetail);

  useEffect(() => {
    const cls = "theme2-dashboard-hide-scrollbars";
    if (theme2HideMainShellScrollbars) {
      document.documentElement.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
    }
    return () => {
      document.documentElement.classList.remove(cls);
    };
  }, [theme2HideMainShellScrollbars]);

  React.useEffect(() => {
    if (shellThemeVariant !== "2" || typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 1200px)");
    const sync = () => {
      if (mq.matches) {
        document.documentElement.classList.remove("nav-open");
        setSidebarOpened(false);
      }
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [shellThemeVariant]);

  if (!token) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: isLightTheme ? '#f8f9fa' : activeColorStyle.background,
        overflow: 'hidden'
      }}>
        <style>
          {`
            @keyframes logoSlide {
              0% { transform: translateX(-20px); opacity: 0; }
              50% { transform: translateX(0); opacity: 1; }
              100% { transform: translateX(20px); opacity: 0; }
            }
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
            .animating-logo {
              animation: logoSlide 2s ease-in-out infinite;
            }
            .shimmer-text {
              color: ${activeColorStyle.borderColor};
              background: linear-gradient(
                to right, 
                ${activeColorStyle.borderColor} 20%, 
                #ffffff 50%, 
                ${activeColorStyle.borderColor} 80%
              );
              background-size: 200% auto;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              animation: shimmer 2s linear infinite;
              display: inline-block;
            }
          `}
        </style>

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <img src="/loading_logo.png" alt="Loading..." className="animating-logo" style={{ width: '100px' }} />
          <div className="shimmer-text" style={{ fontSize: '1.5rem', fontWeight: '800', letterSpacing: '4px', textTransform: 'uppercase', marginLeft: '1rem' }}>
            DealertIQ
          </div>
        </div>


      </div>
    );
  }
  // Token check and redirect (after hooks)
  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.navigationOnly) return null;
      if (prop.layout === "/admin") {
        if (prop.superAdminOnly && account && !account.is_super_admin) {
          return (
            <Route
              path={prop.path}
              element={<Navigate to="/web-maker/preview" replace />}
              key={key}
              exact
            />
          );
        }
        if (
          prop.requiresCommunicationsView &&
          account &&
          !account.is_super_admin &&
          !account.can_communications_view
        ) {
          return (
            <Route
              path={prop.path}
              element={<Navigate to="/dashboard" replace />}
              key={key}
              exact
            />
          );
        }
        if (isWebMakerRouteBlocked(account, prop)) {
          return (
            <Route
              path={prop.path}
              element={<Navigate to="/dashboard" replace />}
              key={key}
              exact
            />
          );
        }
        if (prop.permission && account && !account.is_super_admin && account.permissions) {
          if (!account.permissions[prop.permission]) {
            return (
              <Route
                path={prop.path}
                element={<Navigate to="/dashboard" replace />}
                key={key}
                exact
              />
            );
          }

        }
        return (
          <Route
            path={prop.path}
            element={
              typeof prop.component === 'function'
                ? prop.component({
                  activeColor: resolvedActiveColor,
                  account,
                  isLightTheme,
                  handleActiveClick,
                  sidebarRoutes,
                  sidebarMini,
                  sidebarMiniLayoutReady,
                  shellThemeVariant,
                })
                : prop.component
            }
            key={key}
            exact
          />
        );
      }
      return null;
    });
  };
  const getActiveRoute = (routes) => {
    let activeRoute = "Default Brand Text";
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].views);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.pathname.indexOf(
            routes[i].layout + routes[i].path
          ) !== -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };
  const handleActiveClick = (color) => {
    setActiveColor(color);
    localStorage.setItem("sidebarColor", color);
    updateCSSVariables(color); // CSS değişkenlerini güncelle
  };
  const handleMiniClick = () => {
    if (sidebarCollapseLayoutTimerRef.current) {
      clearTimeout(sidebarCollapseLayoutTimerRef.current);
      sidebarCollapseLayoutTimerRef.current = null;
    }
    setSidebarMini((prev) => {
      const newValue = !prev;
      localStorage.setItem("sidebarMini", JSON.stringify(newValue));
      setSidebarMiniLayoutReady(true);
      return newValue;
    });
  };
  const toggleSidebar = () => {
    const theme2Drawer =
      shellThemeVariant === "2" &&
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1199.98px)").matches;
    if (theme2Drawer && sidebarMini) {
      setSidebarMini(false);
      setSidebarMiniLayoutReady(true);
      localStorage.setItem("sidebarMini", JSON.stringify(false));
    }
    setSidebarOpened(!sidebarOpened);
    document.documentElement.classList.toggle("nav-open");
  };
  const closeSidebar = () => {
    setSidebarOpened(false);
    document.documentElement.classList.remove("nav-open");
  };
  const SidebarShell = shellThemeVariant === "2" ? SidebarTheme2 : SidebarTheme1;
  const NavbarShell = shellThemeVariant === "2" ? AdminNavbarTheme2 : AdminNavbarTheme1;
  const adminNavbarShellProps = {
    ...props,
    location,
    handleMiniClick,
    brandText: getActiveRoute(routes),
    sidebarOpened,
    toggleSidebar,
    closeSidebar,
    activeColor: resolvedActiveColor,
    handleActiveClick,
    sidebarMini,
    routes: sidebarRoutes,
    logo: {
      outterLink: account?.is_super_admin ? "/tenant" : "/dashboard",
      text: "DealertIQ",
      imgSrc: process.env.PUBLIC_URL + "/logo.svg",
    }
  };

  return (
    <NotificationProvider notificationAlertRef={notificationAlertRef}>
      <div className="wrapper">
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        {shellThemeVariant !== "2" && (
          <div className="navbar-minimize-fixed" style={{ opacity: opacity, display: "flex", width: "100%", pointerEvents: "none" }}>
            {/* <button
          className="minimize-sidebar btn btn-link btn-just-icon"
          onClick={handleMiniClick}
        >
          <i className="tim-icons icon-align-center visible-on-sidebar-regular text-muted" />
          <i className="tim-icons icon-bullet-list-67 visible-on-sidebar-mini text-muted" />
        </button> */}
            <button
              className="btn btn-link btn-just-icon"
              onClick={() => navigate("/dashboard")}
              style={{
                padding: "8px",
                // borderRadius: "50%",
                // backgroundColor: "rgba(255,255,255,0.1)",
                border: "none",
                cursor: "pointer",
                pointerEvents: "auto"
              }}
            >
              <img
                src={process.env.PUBLIC_URL + "/logo.svg"}
                alt="DealertIQ Logo"
                style={{
                  width: "50px",
                  height: "50px",
                  filter: "brightness(0) invert(0.7) saturate(0)"
                }}
              />
            </button>
          </div>
        )}
        <SidebarShell
          {...props}
          routes={sidebarRoutes}
          activeColor={resolvedActiveColor}
          logo={{
            outterLink: account?.is_super_admin ? "/tenant" : "/dashboard",
            text: "DealertIQ",
            imgSrc: process.env.PUBLIC_URL + "/logo.svg"
          }}
          sidebarMini={sidebarMini}
          sidebarMiniLayoutReady={sidebarMiniLayoutReady}
          closeSidebar={closeSidebar}
          handleMiniClick={handleMiniClick}
          account={account}
        />
        <div
          className={
            "main-panel" +
            (theme2HideMainShellScrollbars
              ? " theme2-dashboard-main-panel-hide-scrollbar"
              : "")
          }
          style={{ borderTop: "0px solid #1d8cf8" }}
          ref={mainPanelRef}
          data={resolvedActiveColor}
        >
          <NavbarShell {...adminNavbarShellProps} />
          <Routes>
            {getRoutes(routes)}
            <Route path="*" element={<Navigate to={account?.is_super_admin ? "/tenant" : "/dashboard"} replace />} />

          </Routes>
          {
            // we don't want the Footer to be rendered on full screen maps page
            props?.location?.pathname?.indexOf("full-screen-map") !==
              -1 ? null : (
              <Footer fluid activeColor={resolvedActiveColor} isLightTheme={isLightTheme} />
            )
          }
        </div>
        <Footer activeColor={resolvedActiveColor} isLightTheme={isLightTheme} />

        {/* <FixedPlugin
        activeColor={activeColor}
        sidebarMini={sidebarMini}
        handleActiveClick={handleActiveClick}
        handleMiniClick={handleMiniClick}
      /> */}
      </div>
    </NotificationProvider>
  );
};

export default Admin;
